import { useEffect, useState } from 'react';
import { Address } from 'react-daum-postcode';
import { useTranslation } from 'react-i18next';

import { useGetMemberCompanyInfoApi, usePostModifyMemberCompanyInfoApi } from '@/api/mypage';
import Field from '@/components/common/Field/Field';
import InputAddress from '@/components/common/Input/InputAddress/InputAddress';
import InputSelect from '@/components/common/Input/InputSelect/InputSelect';
import InputText from '@/components/common/Input/InputText/InputText';
import { MYPAGE_RAW_POSITION_DIRECT_INPUT } from '@/constants/mypage';
import { positionOptions } from '@/constants/option';
import { useToastStore } from '@/store/toastStore';
import { convertPositionValue } from '@/utils/convertValue';

import * as S from './style';

const CompanyInfo = () => {
	const { t } = useTranslation();
	const { openToast } = useToastStore();
	const [isOpen, setIsOpen] = useState(false);
	const [company, setCompany] = useState({
		companyName: '',
		companyEnName: '',
		position: '',
		positionReason: '',
		phone: '',
	});
	const [address, setAddress] = useState({
		zipcode: '',
		address: '',
		detailAddress: '',
	});

	const { data: companyInfo } = useGetMemberCompanyInfoApi();
	const { mutateAsync: modifyCompanyInfo } = usePostModifyMemberCompanyInfoApi();

	const handleAddress = (val: Address) => {
		setAddress({
			zipcode: val.zonecode,
			address: val.address,
			detailAddress: '',
		});
	};
	const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		const res = await modifyCompanyInfo({
			member_company_name: company.companyName,
			member_company_en_name: company.companyEnName,
			member_position: company.position === MYPAGE_RAW_POSITION_DIRECT_INPUT ? company.positionReason : company.position,
			member_company_tel: company.phone,
			member_company_zipcode: address.zipcode,
			member_company_address: address.address,
			member_company_address_detail: address.detailAddress,
		});
		if (res.data.success) {
			openToast({
				subContent: 'common.societyName',
				content: 'common.toast.infoUpdated',
			});
		}
	};

	useEffect(() => {
		if (companyInfo) {
			setCompany({
				companyName: companyInfo.member_company_name,
				companyEnName: companyInfo.member_company_en_name,
				position: convertPositionValue(companyInfo.member_position),
				positionReason: convertPositionValue(companyInfo.member_position) === MYPAGE_RAW_POSITION_DIRECT_INPUT ? companyInfo.member_position : '',
				phone: companyInfo.member_company_tel,
			});
			setAddress({
				zipcode: companyInfo.member_company_zipcode,
				address: companyInfo.member_company_address,
				detailAddress: companyInfo.member_company_address_detail,
			});
		}
	}, [companyInfo]);

	return (
		<S.FormContainer onSubmit={onSubmit}>
			<S.FieldBox>
				<S.Title>{t('mypage.companyInfo.title')}</S.Title>
				<Field title="mypage.companyInfo.fields.companyNameKo">
					<InputText
						value={company.companyName}
						input={(e: React.ChangeEvent<HTMLInputElement>) => {
							setCompany({ ...company, companyName: e.currentTarget.value });
						}}
					/>
				</Field>
				<Field title="mypage.companyInfo.fields.companyNameEn">
					<InputText
						value={company.companyEnName}
						input={(e: React.ChangeEvent<HTMLInputElement>) => {
							setCompany({ ...company, companyEnName: e.currentTarget.value });
						}}
					/>
				</Field>
				<Field title="common.position">
					<S.AddressContentBox>
						<InputSelect
							options={positionOptions}
							selectedOption={company.position}
							setSelectedOption={(label) => setCompany({ ...company, position: label })}
							defaultValue="mypage.companyInfo.placeholders.position"
						/>
						{company.position === MYPAGE_RAW_POSITION_DIRECT_INPUT && (
							<InputText
								value={company.positionReason}
								input={(e: React.ChangeEvent<HTMLInputElement>) => {
									setCompany({ ...company, positionReason: e.currentTarget.value });
								}}
								placeholder="common.placeholders.directInput"
							/>
						)}
					</S.AddressContentBox>
				</Field>

				<Field title="mypage.companyInfo.fields.phone">
					<InputText
						value={company.phone}
						input={(e: React.ChangeEvent<HTMLInputElement>) => {
							setCompany({ ...company, phone: e.currentTarget.value });
						}}
						placeholder="common.placeholders.phone"
					/>
				</Field>

				<Field title="common.address">
					<S.AddressContentBox>
						<S.AddressBox>
							<InputText readonly value={address.zipcode} />
							<S.FindButton type="button" onClick={() => setIsOpen(true)}>
								{t('common.actions.findZipcode')}
							</S.FindButton>
						</S.AddressBox>
						<S.AddressBox>
							<InputText readonly value={address.address} />
						</S.AddressBox>
						<S.AddressBox>
							<InputText
								value={address.detailAddress}
								input={(e: React.ChangeEvent<HTMLInputElement>) => {
									setAddress({ ...address, detailAddress: e.currentTarget.value });
								}}
								placeholder="common.placeholders.detailAddress"
							/>
						</S.AddressBox>
					</S.AddressContentBox>
					{isOpen && <InputAddress setIsOpen={setIsOpen} setAddress={handleAddress} />}
				</Field>
			</S.FieldBox>
			<S.NextButton type="submit">{t('common.actions.completeUpdate')}</S.NextButton>
		</S.FormContainer>
	);
};

export default CompanyInfo;
